import { useState, useEffect } from 'preact/hooks';
import { route } from 'preact-router';
import type { Order, OrderItem, ItemStatus } from '../lib/types';
import { ITEM_STATUS_LABELS, ITEM_STATUS_COLORS } from '../lib/types';
import * as api from '../lib/api';

interface Props {
  id: string;
  itemIndex: string;
}

export function ItemDetail({ id, itemIndex: itemIndexStr }: Props) {
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const itemIndex = parseInt(itemIndexStr, 10);

  useEffect(() => {
    loadOrder();
  }, [id]);

  async function loadOrder() {
    setLoading(true);
    setError(null);
    try {
      const data = await api.getOrder(id);
      setOrder(data.order);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load order');
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return <div class="text-center py-8 text-gray-500">Loading...</div>;
  }

  if (error || !order) {
    return (
      <div class="text-center py-8">
        <div class="text-red-600 mb-4">{error || 'Order not found'}</div>
        <button
          onClick={() => route('/')}
          class="text-blue-600 hover:underline"
        >
          Back to orders
        </button>
      </div>
    );
  }

  const item = order.items[itemIndex];

  if (!item) {
    return (
      <div class="text-center py-8">
        <div class="text-red-600 mb-4">Item not found</div>
        <button
          onClick={() => route(`/orders/${id}`)}
          class="text-blue-600 hover:underline"
        >
          Back to order
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div class="flex items-center gap-4 mb-6">
        <button
          onClick={() => route(`/orders/${id}`)}
          class="text-gray-500 hover:text-gray-700"
        >
          &larr; Back to Order
        </button>
      </div>

      {/* Item Header */}
      <div class="bg-white rounded-lg shadow p-6 mb-6">
        <div class="flex justify-between items-center">
          <div>
            <h1 class="text-2xl font-bold text-gray-900">
              {item.name || `Item ${itemIndex + 1}`}
            </h1>
            <p class="text-gray-500 mt-1">
              Item {itemIndex + 1} of {order.items.length} in order for {order.buyerName}
            </p>
          </div>
          <span
            class={`px-3 py-1 text-sm rounded-full ${ITEM_STATUS_COLORS[item.status]}`}
          >
            {ITEM_STATUS_LABELS[item.status]}
          </span>
        </div>
      </div>

      {/* Item Workflow */}
      <ItemWorkflow
        order={order}
        item={item}
        itemIndex={itemIndex}
        onUpdate={setOrder}
      />
    </div>
  );
}

function ItemWorkflow({
  order,
  item,
  itemIndex,
  onUpdate,
}: {
  order: Order;
  item: OrderItem;
  itemIndex: number;
  onUpdate: (order: Order) => void;
}) {
  // Show editable form if item is AWAITING_DETAILS
  if (item.status === 'AWAITING_DETAILS') {
    return (
      <ItemDetailsForm
        key={`details-${order.orderId}-${itemIndex}`}
        order={order}
        item={item}
        itemIndex={itemIndex}
        onUpdate={onUpdate}
      />
    );
  }

  return (
    <div>
      {/* Item info */}
      <ItemDetailsReadOnly
        order={order}
        item={item}
        itemIndex={itemIndex}
        onUpdate={onUpdate}
      />

      {/* Origin section */}
      <ContentSection
        title="Origin"
        phase="origin"
        order={order}
        itemIndex={itemIndex}
        content={item.originDraft}
        approved={item.originApproved}
        canGenerate={item.status === 'ORIGIN_DRAFTING'}
        canApprove={item.status === 'ORIGIN_DRAFTING' && !!item.originDraft}
        onUpdate={onUpdate}
      />

      {/* Poem section */}
      <ContentSection
        title="Poem"
        phase="poem"
        order={order}
        itemIndex={itemIndex}
        content={item.poemDraft}
        approved={item.poemApproved}
        canGenerate={
          item.status === 'ORIGIN_APPROVED' || item.status === 'POEM_DRAFTING'
        }
        canApprove={item.status === 'POEM_DRAFTING' && !!item.poemDraft}
        onUpdate={onUpdate}
      />

      {/* File generation */}
      {item.status === 'POEM_APPROVED' && (
        <FileGeneration order={order} itemIndex={itemIndex} onUpdate={onUpdate} />
      )}

      {/* Download links - SVG only (PDF is at order level) */}
      {item.svgUrl && (
        <ProductionFiles
          order={order}
          item={item}
          itemIndex={itemIndex}
          onUpdate={onUpdate}
        />
      )}
    </div>
  );
}

function ItemDetailsReadOnly({
  order,
  item,
  itemIndex,
  onUpdate,
}: {
  order: Order;
  item: OrderItem;
  itemIndex: number;
  onUpdate: (order: Order) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: item.name,
    pronouns: item.pronouns,
    culturalContext: item.culturalContext || '',
    notes: item.notes || '',
    poemThemes: item.poemThemes || '',
    amazonOrderItemId: item.amazonOrderItemId || '',
    frameMaterial: item.frameMaterial || 'cherry',
    needsProof: item.needsProof ?? false,
  });
  const [saving, setSaving] = useState(false);

  async function handleSave() {
    setSaving(true);
    try {
      const result = await api.updateItem(order.orderId, itemIndex, {
        name: formData.name,
        pronouns: formData.pronouns,
        culturalContext: formData.culturalContext || undefined,
        notes: formData.notes || undefined,
        amazonOrderItemId: formData.amazonOrderItemId || undefined,
        frameMaterial: formData.frameMaterial as 'cherry' | 'sapele',
        needsProof: formData.needsProof,
      });
      onUpdate(result.order);
      setEditing(false);
    } catch (err) {
      console.error('Failed to save:', err);
    } finally {
      setSaving(false);
    }
  }

  const displayMaterial = item.frameMaterial || 'cherry';
  const displayNeedsProof = item.needsProof ?? false;

  return (
    <div class="bg-white rounded-lg shadow p-6 mb-6">
      <div class="flex justify-between items-center mb-4">
        <h2 class="text-lg font-semibold">Item Details</h2>
        {!editing && (
          <button
            onClick={() => setEditing(true)}
            class="text-blue-600 hover:underline text-sm"
          >
            Edit
          </button>
        )}
      </div>

      {editing ? (
        <div class="space-y-4">
          <div class="grid grid-cols-2 gap-4">
            <div>
              <label class="block text-sm text-gray-500 mb-1">Name</label>
              <input
                type="text"
                value={formData.name}
                onInput={(e) => setFormData(f => ({ ...f, name: (e.target as HTMLInputElement).value }))}
                class="w-full border rounded px-2 py-1"
              />
            </div>
            <div>
              <label class="block text-sm text-gray-500 mb-1">Pronouns</label>
              <select
                value={formData.pronouns}
                onChange={(e) => setFormData(f => ({ ...f, pronouns: (e.target as HTMLSelectElement).value }))}
                class="w-full border rounded px-2 py-1"
              >
                <option value="he/him">he/him</option>
                <option value="she/her">she/her</option>
                <option value="they/them">they/them</option>
              </select>
            </div>
            <div>
              <label class="block text-sm text-gray-500 mb-1">Frame Material</label>
              <select
                value={formData.frameMaterial}
                onChange={(e) => setFormData(f => ({ ...f, frameMaterial: (e.target as HTMLSelectElement).value as 'cherry' | 'sapele' }))}
                class="w-full border rounded px-2 py-1"
              >
                <option value="cherry">Cherry</option>
                <option value="sapele">Sapele</option>
              </select>
            </div>
            <div>
              <label class="block text-sm text-gray-500 mb-1">Needs Proof</label>
              <label class="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.needsProof}
                  onChange={(e) => setFormData(f => ({ ...f, needsProof: (e.target as HTMLInputElement).checked }))}
                  class="rounded"
                />
                <span class="text-sm">Yes</span>
              </label>
            </div>
            <div>
              <label class="block text-sm text-gray-500 mb-1">Cultural Context</label>
              <textarea
                value={formData.culturalContext}
                onInput={(e) => setFormData(f => ({ ...f, culturalContext: (e.target as HTMLTextAreaElement).value }))}
                class="w-full border rounded px-2 py-1"
                placeholder="e.g., Irish, Japanese"
                rows={3}
              />
            </div>
            <div>
              <label class="block text-sm text-gray-500 mb-1">Notes</label>
              <textarea
                value={formData.notes}
                onInput={(e) => setFormData(f => ({ ...f, notes: (e.target as HTMLTextAreaElement).value }))}
                class="w-full border rounded px-2 py-1"
                placeholder="Production notes (not woven into the poem)"
                rows={3}
              />
            </div>
            <div class="col-span-2">
              <label class="block text-sm text-gray-500 mb-1">
                Poem Themes
                <span class="text-xs text-gray-400 ml-2">— qualities/imagery the buyer wants in the poem</span>
              </label>
              <textarea
                value={formData.poemThemes}
                onInput={(e) => setFormData(f => ({ ...f, poemThemes: (e.target as HTMLTextAreaElement).value }))}
                class="w-full border rounded px-2 py-1"
                placeholder='e.g. "heart of David, sacrifice of Daniel, foresight of Joseph"'
                rows={3}
              />
            </div>
            <div class="col-span-2">
              <label class="block text-sm text-gray-500 mb-1">Amazon Order Item ID</label>
              <input
                type="text"
                value={formData.amazonOrderItemId}
                onInput={(e) => setFormData(f => ({ ...f, amazonOrderItemId: (e.target as HTMLInputElement).value }))}
                class="w-full border rounded px-2 py-1 font-mono text-sm"
                placeholder="e.g., 138335797883201"
              />
            </div>
          </div>
          <div class="flex gap-2">
            <button
              onClick={handleSave}
              disabled={saving}
              class="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button
              onClick={() => {
                setFormData({
                  name: item.name,
                  pronouns: item.pronouns,
                  culturalContext: item.culturalContext || '',
                  notes: item.notes || '',
                  poemThemes: item.poemThemes || '',
                  amazonOrderItemId: item.amazonOrderItemId || '',
                  frameMaterial: item.frameMaterial || 'cherry',
                  needsProof: item.needsProof ?? false,
                });
                setEditing(false);
              }}
              class="text-gray-600 px-4 py-2 hover:text-gray-800"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <dl class="grid grid-cols-2 gap-4 text-sm">
          <div>
            <dt class="text-gray-500">Name</dt>
            <dd class="font-medium">{item.name}</dd>
          </div>
          <div>
            <dt class="text-gray-500">Pronouns</dt>
            <dd>{item.pronouns}</dd>
          </div>
          <div>
            <dt class="text-gray-500">Frame Material</dt>
            <dd class="capitalize">{displayMaterial}</dd>
          </div>
          <div>
            <dt class="text-gray-500">Needs Proof</dt>
            <dd>{displayNeedsProof ? 'Yes' : 'No'}</dd>
          </div>
          {item.culturalContext && (
            <div>
              <dt class="text-gray-500">Cultural Context</dt>
              <dd>{item.culturalContext}</dd>
            </div>
          )}
          {item.notes && (
            <div class="col-span-2">
              <dt class="text-gray-500">Notes</dt>
              <dd>{item.notes}</dd>
            </div>
          )}
          {item.poemThemes && (
            <div class="col-span-2">
              <dt class="text-gray-500">Poem Themes</dt>
              <dd>{item.poemThemes}</dd>
            </div>
          )}
          <div class="col-span-2">
            <dt class="text-gray-500">Amazon Order Item ID</dt>
            <dd class="font-mono">{item.amazonOrderItemId || '(not set)'}</dd>
          </div>
        </dl>
      )}
    </div>
  );
}

function ContentSection({
  title,
  phase,
  order,
  itemIndex,
  content,
  approved,
  canGenerate,
  canApprove,
  onUpdate,
}: {
  title: string;
  phase: 'origin' | 'poem';
  order: Order;
  itemIndex: number;
  content?: string;
  approved?: string;
  canGenerate: boolean;
  canApprove: boolean;
  onUpdate: (order: Order) => void;
}) {
  const [generating, setGenerating] = useState(false);
  const [refining, setRefining] = useState(false);
  const [approving, setApproving] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [editingApproved, setEditingApproved] = useState(false);

  const editableContent = editingApproved ? approved : content;
  const lines = editableContent?.split('\n') || [];
  const MAX_CHARS = 35;

  async function handleGenerate() {
    setGenerating(true);
    setError(null);
    try {
      const result =
        phase === 'origin'
          ? await api.generateOrigin(order.orderId, itemIndex)
          : await api.generatePoem(order.orderId, itemIndex);
      onUpdate(result.order);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Generation failed');
    } finally {
      setGenerating(false);
    }
  }

  async function handleRefine() {
    if (!feedback.trim()) return;
    setRefining(true);
    setError(null);
    try {
      const result =
        phase === 'origin'
          ? await api.refineOrigin(order.orderId, itemIndex, feedback)
          : await api.refinePoem(order.orderId, itemIndex, feedback);
      onUpdate(result.order);
      setFeedback('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Refinement failed');
    } finally {
      setRefining(false);
    }
  }

  async function handleApprove() {
    setApproving(true);
    setError(null);
    try {
      const result =
        phase === 'origin'
          ? await api.approveOrigin(order.orderId, itemIndex)
          : await api.approvePoem(order.orderId, itemIndex);
      onUpdate(result.order);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Approval failed');
    } finally {
      setApproving(false);
    }
  }

  async function handleEditLine(lineIdx: number, newText: string) {
    setError(null);
    try {
      const result =
        phase === 'origin'
          ? await api.editOriginLine(order.orderId, itemIndex, lineIdx, newText)
          : await api.editPoemLine(order.orderId, itemIndex, lineIdx, newText);
      onUpdate(result.order);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Edit failed');
    }
  }

  return (
    <div class="bg-white rounded-lg shadow p-6 mb-6">
      <div class="flex justify-between items-center mb-4">
        <h2 class="text-lg font-semibold">{title}</h2>
        {canApprove && (
          <button
            onClick={handleApprove}
            disabled={approving}
            class="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50"
          >
            {approving ? 'Approving...' : 'Approve'}
          </button>
        )}
        {approved && !editingApproved && (
          <div class="flex items-center gap-3">
            <span class="text-green-600 font-medium">Approved</span>
            <button
              onClick={() => setEditingApproved(true)}
              class="text-gray-500 hover:text-gray-700 text-sm underline"
            >
              Edit
            </button>
          </div>
        )}
        {editingApproved && (
          <button
            onClick={() => setEditingApproved(false)}
            class="text-gray-500 hover:text-gray-700 text-sm"
          >
            Cancel Edit
          </button>
        )}
      </div>

      {error && (
        <div class="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
          {error}
        </div>
      )}

      {approved && !editingApproved && (
        <div class="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
          <pre class="whitespace-pre-wrap text-sm">{approved}</pre>
        </div>
      )}

      {((content && !approved) || editingApproved) && (
        <div class="space-y-2 mb-4">
          {editingApproved && (
            <div class="text-sm text-amber-600 mb-2">
              Editing approved content. Changes will update the approved version.
            </div>
          )}
          {lines.map((line, idx) => (
            <LineEditor
              key={`${idx}-${line}`}
              line={line}
              index={idx}
              maxChars={MAX_CHARS}
              onSave={(newText) => handleEditLine(idx, newText)}
            />
          ))}
          {editingApproved && (
            <div class="pt-2">
              <button
                onClick={() => setEditingApproved(false)}
                class="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
              >
                Done Editing
              </button>
            </div>
          )}
        </div>
      )}

      {canGenerate && !content && (
        <button
          onClick={handleGenerate}
          disabled={generating}
          class="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          {generating ? 'Generating...' : `Generate ${title}`}
        </button>
      )}

      {canGenerate && content && !approved && (
        <div class="border-t pt-4 mt-4">
          <div class="flex gap-2">
            <input
              type="text"
              value={feedback}
              onInput={(e) => setFeedback((e.target as HTMLInputElement).value)}
              placeholder={`How should we adjust the ${title.toLowerCase()}?`}
              class="flex-1 border rounded-lg px-3 py-2"
              onKeyDown={(e) => e.key === 'Enter' && handleRefine()}
            />
            <button
              onClick={handleRefine}
              disabled={refining || !feedback.trim()}
              class="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 disabled:opacity-50"
            >
              {refining ? '...' : 'Refine'}
            </button>
            <button
              onClick={handleGenerate}
              disabled={generating}
              class="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {generating ? '...' : 'Regenerate'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function LineEditor({
  line,
  index,
  maxChars,
  onSave,
}: {
  line: string;
  index: number;
  maxChars: number;
  onSave: (newText: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(line);

  useEffect(() => {
    setText(line);
  }, [line]);

  const charCount = text.length;
  const overLimit = charCount > maxChars;

  function handleSave() {
    if (text !== line) {
      onSave(text);
    }
    setEditing(false);
  }

  if (editing) {
    return (
      <div class="flex items-center gap-2">
        <span class="text-gray-400 w-6">{index + 1}.</span>
        <input
          type="text"
          value={text}
          onInput={(e) => setText((e.target as HTMLInputElement).value)}
          onBlur={handleSave}
          onKeyDown={(e) => e.key === 'Enter' && handleSave()}
          class={`flex-1 border rounded px-2 py-1 ${overLimit ? 'border-red-500' : ''}`}
          autoFocus
        />
        <span class={`text-sm ${overLimit ? 'text-red-600' : 'text-gray-400'}`}>
          {charCount}c
        </span>
      </div>
    );
  }

  return (
    <div
      class="flex items-center gap-2 group cursor-pointer hover:bg-gray-50 rounded px-2 py-1 -mx-2"
      onClick={() => setEditing(true)}
    >
      <span class="text-gray-400 w-6">{index + 1}.</span>
      <span class="flex-1">{line}</span>
      <span class={`text-sm ${overLimit ? 'text-red-600' : 'text-gray-400'}`}>
        [{charCount}c]
      </span>
      <span class="text-gray-400 opacity-0 group-hover:opacity-100">
        &#9998;
      </span>
    </div>
  );
}

function ItemDetailsForm({
  order,
  item,
  itemIndex,
  onUpdate,
}: {
  order: Order;
  item: OrderItem;
  itemIndex: number;
  onUpdate: (order: Order) => void;
}) {
  const [saving, setSaving] = useState(false);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Item-level fields
  const [formData, setFormData] = useState({
    name: item.name,
    pronouns: item.pronouns || 'they/them',
    culturalContext: item.culturalContext || '',
    notes: item.notes || '',
    poemThemes: item.poemThemes || '',
    amazonOrderItemId: item.amazonOrderItemId || '',
    frameMaterial: item.frameMaterial || 'cherry',
    needsProof: item.needsProof ?? false,
  });

  useEffect(() => {
    setFormData({
      name: item.name,
      pronouns: item.pronouns || 'they/them',
      culturalContext: item.culturalContext || '',
      notes: item.notes || '',
      poemThemes: item.poemThemes || '',
      amazonOrderItemId: item.amazonOrderItemId || '',
      frameMaterial: item.frameMaterial || 'cherry',
      needsProof: item.needsProof ?? false,
    });
  }, [item.name, item.pronouns, item.culturalContext, item.notes, item.poemThemes, item.amazonOrderItemId, item.frameMaterial, item.needsProof]);

  async function handleSubmit(e: Event) {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      // Update item-level fields
      const result = await api.updateItem(order.orderId, itemIndex, {
        name: formData.name,
        pronouns: formData.pronouns,
        culturalContext: formData.culturalContext || undefined,
        notes: formData.notes || undefined,
        poemThemes: formData.poemThemes || undefined,
        amazonOrderItemId: formData.amazonOrderItemId || undefined,
        frameMaterial: formData.frameMaterial as 'cherry' | 'sapele',
        needsProof: formData.needsProof,
      });
      onUpdate(result.order);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  async function handleStart() {
    setStarting(true);
    setError(null);

    try {
      // Save item details first
      await api.updateItem(order.orderId, itemIndex, {
        name: formData.name,
        pronouns: formData.pronouns,
        culturalContext: formData.culturalContext || undefined,
        notes: formData.notes || undefined,
        poemThemes: formData.poemThemes || undefined,
        amazonOrderItemId: formData.amazonOrderItemId || undefined,
        frameMaterial: formData.frameMaterial as 'cherry' | 'sapele',
        needsProof: formData.needsProof,
        status: 'ORIGIN_DRAFTING' as ItemStatus,
      });
      // Then generate origin
      const genResult = await api.generateOrigin(order.orderId, itemIndex);
      onUpdate(genResult.order);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start');
    } finally {
      setStarting(false);
    }
  }

  const canStart = formData.name && formData.pronouns;

  return (
    <div class="bg-white rounded-lg shadow p-6 mb-6">
      <h2 class="text-lg font-semibold mb-4">Item Details</h2>

      {error && (
        <div class="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} class="space-y-4">
        <div class="grid grid-cols-2 gap-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              Name (for frame) *
            </label>
            <input
              name="name"
              type="text"
              required
              value={formData.name}
              onInput={(e) => setFormData(f => ({ ...f, name: (e.target as HTMLInputElement).value }))}
              class="w-full border rounded-lg px-3 py-2"
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              Pronouns *
            </label>
            <select
              name="pronouns"
              required
              value={formData.pronouns}
              onChange={(e) => setFormData(f => ({ ...f, pronouns: (e.target as HTMLSelectElement).value }))}
              class="w-full border rounded-lg px-3 py-2"
            >
              <option value="he/him">he/him</option>
              <option value="she/her">she/her</option>
              <option value="they/them">they/them</option>
            </select>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              Cultural Context
            </label>
            <textarea
              name="culturalContext"
              value={formData.culturalContext}
              onInput={(e) => setFormData(f => ({ ...f, culturalContext: (e.target as HTMLTextAreaElement).value }))}
              placeholder="e.g., Irish, Japanese, etc."
              class="w-full border rounded-lg px-3 py-2"
              rows={3}
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              Notes
            </label>
            <textarea
              name="notes"
              value={formData.notes}
              onInput={(e) => setFormData(f => ({ ...f, notes: (e.target as HTMLTextAreaElement).value }))}
              placeholder="Production notes (not woven into the poem)"
              class="w-full border rounded-lg px-3 py-2"
              rows={3}
            />
          </div>

          <div class="col-span-2">
            <label class="block text-sm font-medium text-gray-700 mb-1">
              Poem Themes
              <span class="text-xs text-gray-500 font-normal ml-2">— qualities/imagery the buyer wants in the poem</span>
            </label>
            <textarea
              name="poemThemes"
              value={formData.poemThemes}
              onInput={(e) => setFormData(f => ({ ...f, poemThemes: (e.target as HTMLTextAreaElement).value }))}
              placeholder='e.g. "heart of David, sacrifice of Daniel, foresight of Joseph"'
              class="w-full border rounded-lg px-3 py-2"
              rows={3}
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              Frame Material *
            </label>
            <select
              name="frameMaterial"
              required
              value={formData.frameMaterial}
              onChange={(e) => setFormData(f => ({ ...f, frameMaterial: (e.target as HTMLSelectElement).value as 'cherry' | 'sapele' }))}
              class="w-full border rounded-lg px-3 py-2"
            >
              <option value="cherry">Cherry</option>
              <option value="sapele">Sapele</option>
            </select>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              Needs Proof
            </label>
            <label class="flex items-center gap-2 mt-2">
              <input
                type="checkbox"
                checked={formData.needsProof}
                onChange={(e) => setFormData(f => ({ ...f, needsProof: (e.target as HTMLInputElement).checked }))}
                class="rounded border-gray-300"
              />
              <span class="text-sm text-gray-600">Send proof to customer before production</span>
            </label>
          </div>

          <div class="col-span-2">
            <label class="block text-sm font-medium text-gray-700 mb-1">
              Amazon Order Item ID
            </label>
            <input
              name="amazonOrderItemId"
              type="text"
              value={formData.amazonOrderItemId}
              onInput={(e) => setFormData(f => ({ ...f, amazonOrderItemId: (e.target as HTMLInputElement).value }))}
              placeholder="e.g., 138335797883201"
              class="w-full border rounded-lg px-3 py-2 font-mono text-sm"
            />
          </div>
        </div>

        <div class="flex justify-between pt-4">
          <button
            type="submit"
            disabled={saving}
            class="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Details'}
          </button>

          <button
            type="button"
            onClick={handleStart}
            disabled={starting || !canStart}
            class="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {starting ? 'Starting...' : 'Save & Start Origin'}
          </button>
        </div>
      </form>
    </div>
  );
}

function FileGeneration({
  order,
  itemIndex,
  onUpdate,
}: {
  order: Order;
  itemIndex: number;
  onUpdate: (order: Order) => void;
}) {
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ svgUrl: string } | null>(null);

  async function handleGenerate() {
    setGenerating(true);
    setError(null);
    try {
      const data = await api.generateItemFiles(order.orderId, itemIndex);
      setResult({ svgUrl: data.svgUrl });
      onUpdate(data.order);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'File generation failed');
    } finally {
      setGenerating(false);
    }
  }

  return (
    <div class="bg-white rounded-lg shadow p-6 mb-6">
      <h2 class="text-lg font-semibold mb-4">Generate Production Files</h2>

      {error && (
        <div class="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
          {error}
        </div>
      )}

      {result && (
        <div class="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4">
          SVG generated successfully!
          <div class="mt-2">
            <a
              href={result.svgUrl}
              download
              class="text-blue-600 hover:underline"
            >
              Download SVG
            </a>
          </div>
        </div>
      )}

      {!result && (
        <button
          onClick={handleGenerate}
          disabled={generating}
          class="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 disabled:opacity-50"
        >
          {generating ? 'Generating...' : 'Generate SVG'}
        </button>
      )}
    </div>
  );
}

function ProductionFiles({
  order,
  item,
  itemIndex,
  onUpdate,
}: {
  order: Order;
  item: OrderItem;
  itemIndex: number;
  onUpdate: (order: Order) => void;
}) {
  const [regenerating, setRegenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  async function handleRegenerate() {
    setRegenerating(true);
    setError(null);
    setSuccess(false);
    try {
      const data = await api.generateItemFiles(order.orderId, itemIndex);
      onUpdate(data.order);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Regeneration failed');
    } finally {
      setRegenerating(false);
    }
  }

  return (
    <div class="bg-white rounded-lg shadow p-6 mb-6">
      <h2 class="text-lg font-semibold mb-4">Production Files</h2>

      {error && (
        <div class="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
          {error}
        </div>
      )}

      {success && (
        <div class="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4">
          SVG regenerated successfully!
        </div>
      )}

      <div class="flex items-center gap-4">
        <a
          href={`https://name-frames.link-a-charm.workers.dev/pdf/${item.svgUrl}`}
          download
          class="text-blue-600 hover:underline"
        >
          Download SVG (for production)
        </a>
        <button
          onClick={handleRegenerate}
          disabled={regenerating}
          class="text-gray-600 hover:text-gray-800 text-sm underline disabled:opacity-50"
        >
          {regenerating ? 'Regenerating...' : 'Regenerate SVG'}
        </button>
      </div>
    </div>
  );
}
